// src/components/navigation/TopAppBar.js
import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Button,
  Box,
  Badge,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import party from "party-js";

import useUserProfileStore, {
  selectUserProfileImage,
} from "../../store/useUserProfileStore";
import { useAuth } from "../../contexts/AuthContext";
import { useUserBadges } from "../../contexts/UserBadgesContext";
import useActivityUnreadCount from "../../hooks/useActivityUnreadCount";
import InssaLogo from "../../assets/inssa-logo.webp";
import { useRenderTrace } from "../../dev/renderProfiler";
import { buildAvatarImageAttrs } from "../../utils/imageUrl";
import { APP_SHELL_LAYOUT } from "../../constants/constants";
import { getActiveClaimsCount, subscribeMyRaffleClaims } from "../../services/claimsService";

const routeTitleMap = {
  "/profile/connections/requests": "Friend Requests",
  "/profile/connections": "Friends",
  "/me": "Profile",
  "/home": "Inssa",
  "/watch": "Watch",
  "/series": "Series",
  "/messages": "My Time Capsules",
  "/messages/drop": "Drop Time Capsule",
  "/capsule": "Time Capsule",
  "/business": "Business",
  "/u": "Profile",
  "/profile": "My Profile",
  "/settings": "My Settings",
  "/claims": "My Claims",
  "/coupons": "Coupons",
  "/raffles": "Raffles",
  "/timecapsule": "Bury Time Capule",
  "/pin": "Explore Details",
  "/": "Inssa",
};

function pathMatchesRoute(path, route) {
  if (route === "/") {
    return path === route;
  }

  return path === route || path.startsWith(`${route}/`);
}

function resolveRouteTitle(path) {
  const sortedRoutes = Object.keys(routeTitleMap).sort((a, b) => b.length - a.length);
  const matchedRoute = sortedRoutes.find((route) => pathMatchesRoute(path, route));

  if (matchedRoute) {
    return routeTitleMap[matchedRoute];
  }

  return path.replace("/", "").toUpperCase() || "App";
}

function getSignInPathForLocation(location = {}) {
  const pathname = location.pathname || "/";
  const search = location.search || "";
  const hash = location.hash || "";
  const currentPath = `${pathname}${search}${hash}`;

  if (
    currentPath === "/" ||
    pathname === "/signin" ||
    pathname === "/signup" ||
    pathname === "/phone-otp"
  ) {
    return "/signin";
  }

  return `/signin?next=${encodeURIComponent(currentPath)}`;
}

function asInt(v, fallback = 0) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
}

function getReadableTopBadgeSx(theme, { singleDigit = false } = {}) {
  return {
    backgroundColor: theme.colors.green,
    color: theme.colors.white,
    height: 24,
    minWidth: singleDigit ? 24 : 28,
    padding: singleDigit ? 0 : "0 8px",
    fontSize: "0.78rem",
    fontWeight: 950,
    lineHeight: 1,
    letterSpacing: 0,
    borderRadius: 999,
    border: `2px solid ${theme.palette.common.white}`,
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.34)}`,
    top: 2,
    left: 4,
    transform: "translate(-90%, -40%)",
    transformOrigin: "100% 50%",
    whiteSpace: "nowrap",
    maxWidth: "none",
  };
}

const PointsButton = React.memo(function PointsButton({ userId, pointsLedgerHref }) {
  // Points total comes from the session-level UserBadges listener instead of a
  // per-mount Firestore subscription here.
  const { pointsTotal } = useUserBadges();
  const [pointsDisplay, setPointsDisplay] = React.useState(0);
  const pointsAnchorRef = React.useRef(null);

  // Animation refs (ONLY used when SuccessDialog tells us to animate)
  const isAnimatingRef = React.useRef(false);
  const rafRef = React.useRef(0);
  const lastExplodedRef = React.useRef(0);

  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  React.useEffect(() => {
    if (!userId) {
      setPointsDisplay(0);
    }
  }, [userId]);

  // Keep badge in sync with total WITHOUT animating (prevents party on refresh/navigation)
  React.useEffect(() => {
    if (isAnimatingRef.current) return;
    setPointsDisplay(asInt(pointsTotal, 0));
  }, [pointsTotal]);

  const animatePointsTo = React.useCallback(
    (targetTotal) => {
      const to = asInt(targetTotal, 0);

      // If reduced motion, no animation, no confetti
      if (reduceMotion) {
        isAnimatingRef.current = false;
        setPointsDisplay(to);
        return;
      }

      // Cancel any in-flight animation
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      isAnimatingRef.current = true;

      setPointsDisplay((fromVal) => {
        const from = asInt(fromVal, 0);
        if (from === to) {
          isAnimatingRef.current = false;
          return fromVal;
        }

        const durationMs = 650;
        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
        let start = null;

        const tick = (ts) => {
          if (!start) start = ts;
          const p = Math.min(1, (ts - start) / durationMs);
          const eased = easeOutCubic(p);
          const next = Math.floor(from + (to - from) * eased);
          setPointsDisplay(next);

          if (p < 1) {
            rafRef.current = requestAnimationFrame(tick);
          } else {
            isAnimatingRef.current = false;

            // Confetti ONLY when explicitly animated (i.e. SuccessDialog told us to)
            const now = Date.now();
            if (now - lastExplodedRef.current > 1000) {
              lastExplodedRef.current = now;
              const el = pointsAnchorRef.current;
              if (el) {
                party.confetti(el, {
                  count: party.variation.range(22, 34),
                  spread: 60,
                  size: party.variation.range(0.85, 1.2),
                  shapes: ["star"],
                });
              }
            }

            // If snapshot total differs (race), snap to it after animation
            const snapTo = asInt(pointsTotal, 0);
            if (snapTo !== to) setPointsDisplay(snapTo);
          }
        };

        rafRef.current = requestAnimationFrame(tick);
        return fromVal;
      });
    },
    [pointsTotal, reduceMotion]
  );

  // Listen for SuccessDialog -> TopAppBar signal
  React.useEffect(() => {
    const handler = (e) => {
      const nextTotal = e?.detail?.totalPoints;
      if (!Number.isFinite(Number(nextTotal))) return;
      animatePointsTo(nextTotal);
    };

    window.addEventListener("inssa:points-updated", handler);
    return () => {
      window.removeEventListener("inssa:points-updated", handler);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animatePointsTo]);

  if (!userId) {
    return null;
  }

  const visiblePointsCount = asInt(pointsDisplay, 0);
  const pointsBadgeContent =
    visiblePointsCount > 99 ? "99+" : visiblePointsCount >= 1 ? String(visiblePointsCount) : null;
  const pointsIcon = (
    <Box
      ref={pointsAnchorRef}
      sx={(theme) => ({
        width: 40,
        height: 40,
        borderRadius: "50%",
        backgroundColor: theme.colors.mediumBlue,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      })}
    >
      <WorkspacePremiumIcon sx={(theme) => ({ color: theme.colors.white })} />
    </Box>
  );

  return (
    <IconButton
      component={Link}
      to={pointsLedgerHref}
      color="inherit"
      aria-label="Points ledger"
      sx={{
        p: 0,
        borderRadius: 999,
      }}
    >
      {visiblePointsCount >= 1 ? (
        <Badge
          badgeContent={pointsBadgeContent}
          overlap="circular"
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          sx={(theme) => ({
            "& .MuiBadge-badge": {
              ...getReadableTopBadgeSx(theme, { singleDigit: pointsBadgeContent.length === 1 }),
            },
          })}
        >
          {pointsIcon}
        </Badge>
      ) : (
        pointsIcon
      )}
    </IconButton>
  );
});

const TopAppBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser: user } = useAuth();
  const [hideTimeCapsuleBackButton, setHideTimeCapsuleBackButton] = React.useState(false);

  const userProfileImage = useUserProfileStore(selectUserProfileImage);
  const { unreadCount: unreadActivityCount } = useActivityUnreadCount(user?.uid || "");
  useRenderTrace("TopAppBar", {
    path: location.pathname,
    userId: user?.uid ?? null,
    hasProfileImage: Boolean(userProfileImage),
  });

  // Title from route
  const path = location.pathname;
  const isWizardRoute = path === "/timecapsule";
  const title = resolveRouteTitle(path);
  const signInPath = React.useMemo(() => getSignInPathForLocation(location), [location]);

  const showBackButton = path !== "/" && !(isWizardRoute && hideTimeCapsuleBackButton);

  const avatarSrc = userProfileImage || (user && user.photoURL) || "";
  const avatarImage = React.useMemo(
    () => buildAvatarImageAttrs(avatarSrc, { size: 40 }),
    [avatarSrc]
  );

  // Back button with safe fallback
  const handleBackClick = React.useCallback(() => {
    if (path === "/timecapsule") {
      const backEvent = new CustomEvent("inssa:timecapsule-back", {
        detail: { handled: false },
      });
      window.dispatchEvent(backEvent);
      if (backEvent.detail?.handled) return;
    }

    if (path.startsWith("/capsule/")) {
      const source = String(location.state?.source || "").trim();
      const returnCenter = location.state?.returnCenter;
      const hasReturnCenter =
        returnCenter &&
        Number.isFinite(returnCenter.lat) &&
        Number.isFinite(returnCenter.lng);

      if (source === "home-map" && hasReturnCenter) {
        navigate("/", { state: { center: returnCenter } });
        return;
      }
    }

    const canGoBack =
      typeof window !== "undefined" &&
      window.history &&
      window.history.state &&
      typeof window.history.state.idx === "number" &&
      window.history.state.idx > 0;

    if (canGoBack) navigate(-1);
    else navigate("/");
  }, [location.state, navigate, path]);

  React.useEffect(() => {
    const handleWizardBackVisibility = (event) => {
      setHideTimeCapsuleBackButton(Boolean(event?.detail?.hidden));
    };

    window.addEventListener("inssa:timecapsule-back-visibility", handleWizardBackVisibility);
    return () => {
      window.removeEventListener("inssa:timecapsule-back-visibility", handleWizardBackVisibility);
    };
  }, []);

  React.useEffect(() => {
    if (path !== "/timecapsule" && hideTimeCapsuleBackButton) {
      setHideTimeCapsuleBackButton(false);
    }
  }, [hideTimeCapsuleBackButton, path]);

  const [activeClaimsCount, setActiveClaimsCount] = React.useState(0);

  React.useEffect(() => {
    if (!user?.uid) {
      setActiveClaimsCount(0);
      return undefined;
    }

    return subscribeMyRaffleClaims({
      userUid: user.uid,
      onNext: (claims) => setActiveClaimsCount(getActiveClaimsCount(claims)),
      onError: (error) => {
        console.warn("[claims] Failed to refresh claim count:", error);
        setActiveClaimsCount(0);
      },
    });
  }, [user?.uid]);

  // route stub for future ledger page
  const pointsLedgerHref = "/points-ledger";
  const topSafeInset = APP_SHELL_LAYOUT.SAFE_AREA_TOP_CSS;
  const visibleUnreadActivityCount = asInt(unreadActivityCount, 0);
  const unreadActivityBadgeLabel =
    visibleUnreadActivityCount > 99
      ? "99+"
      : visibleUnreadActivityCount >= 1
        ? String(visibleUnreadActivityCount)
        : null;
  const singleDigitActivityBadge =
    visibleUnreadActivityCount > 0 && visibleUnreadActivityCount < 10;
  const activeClaimsBadgeCount = asInt(activeClaimsCount, 0);
  const activeClaimsBadgeLabel =
    activeClaimsBadgeCount > 99
      ? "99+"
      : activeClaimsBadgeCount >= 1
        ? String(activeClaimsBadgeCount)
        : null;

  return (
    <AppBar
      position="fixed"
      color="primary"
      sx={(theme) => ({
        boxShadow: "none",
        height: APP_SHELL_LAYOUT.TOP_APP_BAR_OFFSET_CSS,
        display: "flex",
        justifyContent: "flex-end",
        paddingTop: topSafeInset,
        paddingBottom: "15px",
        backgroundColor: theme.colors.topAppBarBg,
        backgroundImage: "none",
      })}
    >
      <Toolbar
        sx={{
          width: "100%",
          alignItems: "flex-end",
          minHeight: "unset",
          position: "relative",
          px: 1,
        }}
      >
        {/* Left slot */}
        <Box sx={{ width: showBackButton ? 40 : 8, display: "flex", alignItems: "center" }}>
          {showBackButton ? (
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleBackClick}
              aria-label="Back"
              sx={{ ml: -0.5 }}
            >
              <ArrowBackIcon />
            </IconButton>
          ) : null}
        </Box>

        {/* Left-aligned title with logo and truncation to avoid squeezing */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            ml: -0.25,
            mr: 0.5,
            paddingBottom: "5px",
            position: "relative",
            top: -2,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minHeight: 32,
          }}
        >
          <Box
            component="img"
            src={InssaLogo}
            alt="Inssa"
            sx={{
              width: 26,
              height: 26,
              objectFit: "contain",
              flex: "0 0 auto",
              borderRadius: 0.75,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              minWidth: 0,
              textAlign: "left",
              fontSize: { xs: "0.95rem", sm: "1.05rem" },
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              pointerEvents: "none",
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Right slot */}
        <Box
          sx={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            paddingBottom: "2px",
          }}
        >
          {!isWizardRoute && (
            <>
              {/* POINTS (clickable) */}
              <PointsButton userId={user?.uid ?? null} pointsLedgerHref={pointsLedgerHref} />

              {/* Claims */}
              {user ? (
                <IconButton
                  component={Link}
                  to="/claims"
                  color="inherit"
                  aria-label="My Claims"
                  sx={{
                    p: 0,
                    borderRadius: 999,
                  }}
                >
                  {activeClaimsBadgeCount >= 1 ? (
                    <Badge
                      badgeContent={activeClaimsBadgeLabel}
                      overlap="circular"
                      anchorOrigin={{ vertical: "top", horizontal: "left" }}
                      sx={(theme) => ({
                        "& .MuiBadge-badge": {
                          ...getReadableTopBadgeSx(theme, { singleDigit: activeClaimsBadgeLabel.length === 1 }),
                        },
                      })}
                    >
                      <Box
                        sx={(theme) => ({
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: theme.colors.mediumBlue,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        })}
                      >
                        <ConfirmationNumberOutlinedIcon sx={(theme) => ({ color: theme.colors.white })} />
                      </Box>
                    </Badge>
                  ) : (
                    <Box
                      sx={(theme) => ({
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: theme.colors.mediumBlue,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      })}
                    >
                      <ConfirmationNumberOutlinedIcon sx={(theme) => ({ color: theme.colors.white })} />
                    </Box>
                  )}
                </IconButton>
              ) : null}

              {/* Avatar */}
              {user ? (
                <Link
                  to="/me"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  {visibleUnreadActivityCount >= 1 ? (
                    <Badge
                      overlap="circular"
                      badgeContent={unreadActivityBadgeLabel}
                      anchorOrigin={{ vertical: "top", horizontal: "left" }}
                      sx={(theme) => ({
                        ml: "10px",
                        "& .MuiBadge-badge": {
                          ...getReadableTopBadgeSx(theme, { singleDigit: singleDigitActivityBadge }),
                        },
                      })}
                    >
                      <Avatar
                        alt="User Avatar"
                        src={avatarImage.src || undefined}
                        imgProps={{
                          srcSet: avatarImage.srcSet || undefined,
                          sizes: avatarImage.sizes || undefined,
                          loading: "lazy",
                          decoding: "async",
                        }}
                      >
                        {user.displayName ? user.displayName[0].toUpperCase() : ""}
                      </Avatar>
                    </Badge>
                  ) : (
                    <Avatar
                      alt="User Avatar"
                      src={avatarImage.src || undefined}
                      imgProps={{
                        srcSet: avatarImage.srcSet || undefined,
                        sizes: avatarImage.sizes || undefined,
                        loading: "lazy",
                        decoding: "async",
                      }}
                      sx={{ ml: "10px" }}
                    >
                      {user.displayName ? user.displayName[0].toUpperCase() : ""}
                    </Avatar>
                  )}
                </Link>
              ) : (
                <Box>
                  <Button
                    color="inherit"
                    component={Link}
                    to={signInPath}
                    sx={{ textTransform: "none" }}
                  >
                    Sign In
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopAppBar;
